/* ═══════════════════════════════════════════════════════════
   FF • Announcement Bar
   Direction-aware transitions + stable arrow positioning
   ═══════════════════════════════════════════════════════════ */

class FFAnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.autoplayTimer = null;
    this.isPaused = false;
    this.isAnimating = false;
    
    // Touch/drag state
    this.touchStartX = 0;
    this.touchCurrentX = 0;
    this.isDragging = false;
  }
  
  connectedCallback() {
    this.slides = this.querySelectorAll('.ff-announce__slide');
    this.slidesContainer = this.querySelector('.ff-announce__slides');
    this.prevBtn = this.querySelector('.ff-announce__arrow--prev');
    this.nextBtn = this.querySelector('.ff-announce__arrow--next');
    this.dots = this.querySelectorAll('.ff-announce__dot');
    
    if (!this.slides.length) return;
    
    this.totalSlides = this.slides.length;
    this.autoplay = this.dataset.autoplay === 'true';
    this.speed = parseInt(this.dataset.speed, 10) || 5000;
    this.pauseOnHover = this.dataset.pauseOnHover !== 'false';
    
    // Get transition duration from CSS
    const style = getComputedStyle(this);
    const transitionValue = style.getPropertyValue('--ff-announce-transition').trim();
    this.transitionDuration = parseInt(transitionValue, 10) || 400;
    
    this.init();
  }
  
  disconnectedCallback() {
    this.stopAutoplay();
    if (this.boundResize) {
      window.removeEventListener('resize', this.boundResize);
    }
  }
  
  init() {
    // Ensure first slide is active
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === 0);
    });
    
    // Set fixed width based on widest slide (desktop only)
    this.setContainerWidth();
    
    // Re-measure on resize
    this.boundResize = this.setContainerWidth.bind(this);
    window.addEventListener('resize', this.boundResize);
    
    this.setupArrows();
    this.setupDots();
    this.setupSwipe();
    this.setupPauseOnHover();
    this.setupKeyboard();
    
    if (this.autoplay && this.totalSlides > 1) {
      this.startAutoplay();
    }
  }
  
  setContainerWidth() {
    if (!this.slidesContainer || this.slides.length === 0) return;
    
    // Only set fixed width on desktop (mobile arrows are at viewport edges)
    const isMobile = window.innerWidth < 750;
    if (isMobile) {
      this.slidesContainer.style.width = '';
      return;
    }
    
    // Reset width first
    this.slidesContainer.style.width = '';
    
    // Temporarily make all slides measurable
    const originalStates = [];
    this.slides.forEach((slide) => {
      originalStates.push({
        position: slide.style.position,
        visibility: slide.style.visibility,
        opacity: slide.style.opacity
      });
      slide.style.position = 'relative';
      slide.style.visibility = 'visible';
      slide.style.opacity = '1';
    });
    
    // Find widest slide
    let maxWidth = 0;
    this.slides.forEach(slide => {
      const width = slide.offsetWidth;
      if (width > maxWidth) maxWidth = width;
    });
    
    // Restore original states
    this.slides.forEach((slide, i) => {
      const isActive = i === this.currentIndex;
      slide.style.position = isActive ? 'relative' : 'absolute';
      slide.style.visibility = isActive ? 'visible' : 'hidden';
      slide.style.opacity = isActive ? '1' : '0';
    });
    
    // Set fixed width
    if (maxWidth > 0) {
      this.slidesContainer.style.width = maxWidth + 'px';
    }
  }
  
  setupArrows() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prev());
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next());
    }
  }
  
  setupDots() {
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        const direction = index > this.currentIndex ? 'next' : 'prev';
        this.goTo(index, direction);
      });
    });
  }
  
  setupSwipe() {
    if (!this.slidesContainer || this.totalSlides < 2) return;
    
    this.slidesContainer.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: true });
    this.slidesContainer.addEventListener('touchmove', (e) => this.onTouchMove(e), { passive: true });
    this.slidesContainer.addEventListener('touchend', () => this.onTouchEnd());
    
    this.slidesContainer.addEventListener('mousedown', (e) => this.onDragStart(e));
    document.addEventListener('mousemove', (e) => this.onDragMove(e));
    document.addEventListener('mouseup', () => this.onDragEnd());
  }
  
  setupPauseOnHover() {
    if (!this.pauseOnHover || !this.autoplay) return;
    
    this.addEventListener('mouseenter', () => {
      this.isPaused = true;
      this.stopAutoplay();
    });
    
    this.addEventListener('mouseleave', () => {
      this.isPaused = false;
      if (this.autoplay) this.startAutoplay();
    });
    
    this.addEventListener('focusin', () => {
      this.isPaused = true;
      this.stopAutoplay();
    });
    
    this.addEventListener('focusout', () => {
      this.isPaused = false;
      if (this.autoplay) this.startAutoplay();
    });
  }
  
  setupKeyboard() {
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.next();
      }
    });
  }
  
  onTouchStart(e) {
    if (this.isAnimating) return;
    this.touchStartX = e.touches[0].clientX;
    this.touchCurrentX = this.touchStartX;
    this.isDragging = true;
  }
  
  onTouchMove(e) {
    if (!this.isDragging) return;
    this.touchCurrentX = e.touches[0].clientX;
  }
  
  onTouchEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    
    const diff = this.touchCurrentX - this.touchStartX;
    const threshold = 50;
    
    if (diff > threshold) {
      this.prev();
    } else if (diff < -threshold) {
      this.next();
    }
  }
  
  onDragStart(e) {
    if (this.isAnimating || e.target.closest('.ff-announce__arrow')) return;
    this.touchStartX = e.clientX;
    this.touchCurrentX = e.clientX;
    this.isDragging = true;
    e.preventDefault();
  }
  
  onDragMove(e) {
    if (!this.isDragging) return;
    this.touchCurrentX = e.clientX;
  }
  
  onDragEnd() {
    if (!this.isDragging) return;
    this.isDragging = false;
    
    const diff = this.touchCurrentX - this.touchStartX;
    const threshold = 50;
    
    if (diff > threshold) {
      this.prev();
    } else if (diff < -threshold) {
      this.next();
    }
  }
  
  prev() {
    if (this.isAnimating || this.totalSlides < 2) return;
    const newIndex = this.currentIndex === 0 ? this.totalSlides - 1 : this.currentIndex - 1;
    this.goTo(newIndex, 'prev');
  }
  
  next() {
    if (this.isAnimating || this.totalSlides < 2) return;
    const newIndex = this.currentIndex === this.totalSlides - 1 ? 0 : this.currentIndex + 1;
    this.goTo(newIndex, 'next');
  }
  
  goTo(index, direction = 'next') {
    if (index === this.currentIndex || this.isAnimating) return;
    
    this.isAnimating = true;
    
    const currentSlide = this.slides[this.currentIndex];
    const nextSlide = this.slides[index];
    
    // Clear animation classes
    this.slides.forEach(slide => {
      slide.classList.remove('enter-from-left', 'enter-from-right', 'exit-to-left', 'exit-to-right', 'is-exiting');
    });
    
    // Position incoming slide
    nextSlide.style.transition = 'none';
    nextSlide.style.position = 'absolute';
    nextSlide.style.visibility = 'visible';
    nextSlide.style.opacity = '0';
    
    if (direction === 'next') {
      nextSlide.classList.add('enter-from-right');
    } else {
      nextSlide.classList.add('enter-from-left');
    }
    
    // Force reflow
    void nextSlide.offsetWidth;
    
    // Re-enable transition
    nextSlide.style.transition = '';
    
    // Exit current slide
    currentSlide.classList.add('is-exiting');
    if (direction === 'next') {
      currentSlide.classList.add('exit-to-left');
    } else {
      currentSlide.classList.add('exit-to-right');
    }
    
    // Activate incoming slide
    nextSlide.classList.add('is-active');
    nextSlide.classList.remove('enter-from-left', 'enter-from-right');
    nextSlide.style.position = '';
    nextSlide.style.visibility = '';
    nextSlide.style.opacity = '';
    
    // Cleanup
    setTimeout(() => {
      currentSlide.classList.remove('is-active', 'is-exiting', 'exit-to-left', 'exit-to-right');
      currentSlide.style.position = 'absolute';
      currentSlide.style.visibility = 'hidden';
      currentSlide.style.opacity = '0';
      
      this.currentIndex = index;
      this.updateDots();
      this.isAnimating = false;
      
      if (this.autoplay && !this.isPaused) {
        this.stopAutoplay();
        this.startAutoplay();
      }
    }, this.transitionDuration);
  }
  
  updateDots() {
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.currentIndex);
      dot.setAttribute('aria-selected', i === this.currentIndex);
    });
  }
  
  startAutoplay() {
    if (this.autoplayTimer || this.totalSlides < 2) return;
    
    this.autoplayTimer = setInterval(() => {
      if (!this.isPaused && !this.isAnimating) {
        this.next();
      }
    }, this.speed);
  }
  
  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
}

if (!customElements.get('ff-announcement-bar')) {
  customElements.define('ff-announcement-bar', FFAnnouncementBar);
}